import { Gantt, Identifiable, List, Project } from "@planzone/dto";
import { gantt } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { calculateAdvancement } from "../../utils/calculateAdvancement";
import { reduceGantt } from "../../common/gantt";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function calculateTasksAdvancement(
  list: Identifiable<List>,
): Identifiable<List> {
  return {
    ...list,
    tasks: list.tasks.map(calculateAdvancement),
    subLists: list.subLists.map(calculateTasksAdvancement),
  };
}

function handleRequest(state: State, project: Identifiable<Project>): State {
  return [...state, { request: setRequestStateToPending(), project, gantt }];
}

function handleSuccess(
  state: State,
  { project, gantt }: { project: Identifiable<Project>; gantt: Gantt },
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? {
          ...res,
          request: setRequestStateToSuccess(),
          project,
          gantt: {
            ...gantt,
            projects: gantt.projects.map((project) => ({
              ...project,
              lists: project.lists.map(calculateTasksAdvancement),
            })),
          },
        }
      : res,
  );
}

function handleFailure(state: State, project: Identifiable<Project>): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? { ...res, request: setRequestStateToFailure() }
      : res,
  );
}

function handleConstrainElementSucceeded(
  state: State,
  {}: Boundaries["duplicateList"]["success"],
): State {
  return initialState;
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((res) => ({
    ...res,
    gantt: reduceGantt(res.gantt, event),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveProjectGanttRequested]: handleRequest,
      [events.retrieveProjectGanttSucceeded]: handleSuccess,
      [events.retrieveProjectGanttFailed]: handleFailure,
      [events.duplicateListSucceeded]: handleConstrainElementSucceeded,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
